import {ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {AdminForwardingService} from '../../control/admin-forwarding.service';
import {ForwardConfig, ForwardingStatus} from '../../entity/forwarding.model';
import {LocalizationPipePipe} from '../../../localization/control/localisation-pipe-pipe';
import {LocalizationService} from '../../../localization/control/localization.service';

@Component({
  selector: 'app-forwarding-form-config',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, LocalizationPipePipe],
  templateUrl: './forwarding-form-config.html',
  styleUrl: './forwarding-form-config.scss'
})
export class ForwardingFormConfig implements OnInit {
  status: ForwardingStatus | undefined;
  configForm: FormGroup;
  loading = true;
  saving = false;
  error: string | undefined;
  successMessage: string | undefined;
  formId = '';
  version = '';

  constructor(
    private readonly adminForwardingService: AdminForwardingService,
    private readonly route: ActivatedRoute,
    private readonly fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef,
    private readonly localizationService: LocalizationService
  ) {
    this.configForm = this.fb.group({
      dailyLimit: [0],
      maxRetries: [0],
      batchSize: [1],
      isEnabled: [true]
    });
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.formId = params['formId'] ?? '';
      this.version = params['version'] ?? '';
      this.loadStatus().then();
    });
  }

  async loadStatus(): Promise<void> {
    this.loading = true;
    this.error = undefined;
    this.cdr.detectChanges();
    try {
      this.status = await this.adminForwardingService.getForwardingStatus(this.formId, this.version);
      this.configForm.patchValue({
        dailyLimit: this.status.config.dailyLimit,
        maxRetries: this.status.config.maxRetries,
        batchSize: this.status.config.batchSize,
        isEnabled: this.status.config.isEnabled
      });
    } catch (err: unknown) {
      this.error = err instanceof Error ? err.message : this.localizationService.getTranslation('admin.forwarding.error.load');
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  async onSave(): Promise<void> {
    this.saving = true;
    this.error = undefined;
    this.successMessage = undefined;
    this.cdr.detectChanges();
    try {
      const config: ForwardConfig = {
        dailyLimit: this.configForm.value.dailyLimit,
        maxRetries: this.configForm.value.maxRetries,
        batchSize: this.configForm.value.batchSize,
        isEnabled: this.configForm.value.isEnabled
      };
      await this.adminForwardingService.updateConfig(this.formId, this.version, config);
      this.successMessage = this.localizationService.getTranslation('admin.forwarding.config.saveSuccess');
    } catch (err: unknown) {
      this.error = err instanceof Error ? err.message : this.localizationService.getTranslation('admin.forwarding.error.action');
    } finally {
      this.saving = false;
      this.cdr.detectChanges();
    }
  }

  async onResetCounter(): Promise<void> {
    this.error = undefined;
    this.successMessage = undefined;
    this.cdr.detectChanges();
    try {
      await this.adminForwardingService.resetDailyCounter(this.formId, this.version);
      this.successMessage = this.localizationService.getTranslation('admin.forwarding.counter.resetSuccess');
      await this.loadStatus();
    } catch (err: unknown) {
      this.error = err instanceof Error ? err.message : this.localizationService.getTranslation('admin.forwarding.error.action');
    } finally {
      this.cdr.detectChanges();
    }
  }
}
